import { Alert, Box, LinearProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { usePendo } from "../../modules/analytics/hooks/use-pendo-watch";
import { useClaims } from "../../modules/generic/hooks/use-claims";
import { Page } from "../../modules/layout/components/page";
import { ASSISTANTS } from "../../modules/mcp-connect/assistants";
import { ConnectWizard } from "../../modules/mcp-connect/connect-wizard";
import { connectionUrl } from "../../modules/mcp-connect/connection-url";
import { McpConnectLanding } from "../../modules/mcp-connect/mcp-connect-landing";
import { useMcpConnectStatus } from "../../modules/mcp-connect/mcp-connect-status";
import { McpConnectedView } from "../../modules/mcp-connect/mcp-connected-view";
import { shortDate } from "../../modules/mcp-connect/short-date";
import { usePermissions } from "../../server/auth/use-permissions";
import { useFlags } from "../../server/optimizely";
import { getOrganization } from "../../utils/organization";
import NotFound from "../not-found";

import type { Assistant } from "../../modules/mcp-connect/assistants";
import type { EntryPoint } from "../../modules/mcp-connect/mcp-connect-landing";
import type { FC } from "react";

const AiPage: FC = () => {
  const {
    flags: { changeengine_mcp: changeengineMcp },
    flagsLoaded,
  } = useFlags("changeengine_mcp");
  const pendo = usePendo();
  const claims = useClaims();
  const { ce_admin: isCeAdmin } = usePermissions();
  const { status, loading, error, userId, agree, complete, reset } = useMcpConnectStatus();
  const [wizardAssistant, setWizardAssistant] = useState<Assistant | null>(null);

  useEffect(() => {
    if (changeengineMcp) pendo?.track("mcp-connect-page-viewed");
  }, [changeengineMcp, pendo]);

  if (!flagsLoaded) return <LinearProgress />;
  if (!changeengineMcp) return <NotFound />;

  const acceptance = status?.dataSharing.acceptance ?? null;
  const acceptedBy = acceptance
    ? acceptance.acceptedByUserId === userId
      ? "you"
      : (acceptance.acceptedByName ?? "an admin")
    : null;
  const agreement =
    acceptance && acceptedBy ? { by: acceptedBy, on: shortDate(acceptance.acceptedAt) } : null;
  const agreedOn = agreement ? `${agreement.by} · ${agreement.on}` : null;

  const connection = connectionUrl(
    claims?.org_name || getOrganization(new URLSearchParams(location.search)),
  );
  const hasConnected = ASSISTANTS.some(({ id }) => status?.setups[id]?.completedAt);

  const start = (entryPoint: EntryPoint, assistant: Assistant): void => {
    pendo?.track("mcp-connect-setup-started", { entry_point: entryPoint, assistant });
    setWizardAssistant(assistant);
  };

  return (
    <Page title="ChangeEngine MCP | ChangeEngine" data-testid="mcp-connect-page">
      {error ? (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error">Couldn’t load your setup status. Please reload the page.</Alert>
        </Box>
      ) : null}
      {loading ? (
        <LinearProgress />
      ) : hasConnected && status ? (
        <McpConnectedView
          setups={status.setups}
          connection={connection}
          agreedLine={agreedOn ? `Data sharing agreed for your organization by ${agreedOn}` : null}
          onStart={start}
          onDisconnect={reset}
        />
      ) : (
        <McpConnectLanding
          agreedLine={agreedOn ? `Data sharing agreed by ${agreedOn}` : null}
          onStart={start}
        />
      )}
      {wizardAssistant ? (
        <ConnectWizard
          initialAssistant={wizardAssistant}
          connection={connection}
          agreement={agreement}
          canAgree={Boolean(acceptance) || !isCeAdmin || status?.clientAccount === false}
          onAgree={agree}
          onComplete={complete}
          onClose={() => setWizardAssistant(null)}
        />
      ) : null}
    </Page>
  );
};

export default AiPage;
